/**
 * Non-sensitive UI preferences (toggle states such as "live sun on/off").
 *
 * Auth/session data is NOT stored here — sessions live in an httpOnly cookie
 * managed by the backend. Everything in this module is harmless if read by a
 * third party and is safe to lose (Safari private mode, quota errors, SSR).
 */
const memoryFallback = new Map();

const storage = () => {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
};

export const readPref = (key, fallback = null) => {
  const store = storage();
  if (!store) return memoryFallback.has(key) ? memoryFallback.get(key) : fallback;
  try {
    const value = store.getItem(key);
    return value === null ? fallback : value;
  } catch (err) {
    // Storage blocked (private mode / disabled) — fall back to in-memory value.
    return memoryFallback.has(key) ? memoryFallback.get(key) : fallback;
  }
};

export const writePref = (key, value) => {
  memoryFallback.set(key, value);
  const store = storage();
  if (!store) return false;
  try {
    store.setItem(key, value);
    return true;
  } catch (err) {
    // Quota exceeded or storage disabled: the in-memory copy keeps the UI consistent
    // for this session, so a failed write is not an error worth surfacing.
    return false;
  }
};

export const readBoolPref = (key, fallback) => {
  const v = readPref(key, null);
  if (v === null) return fallback;
  return v === '1';
};

export const writeBoolPref = (key, value) => writePref(key, value ? '1' : '0');
